import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { FilterMatchMode } from 'primereact/api';
import { translatedMessage } from '../../service/LanguageService';
import GeneralUtils from '../../utilities/GeneralUtils';
import DataTableUtils from '../../utilities/DataTableUtils';
import useAuth from "../../hooks/useAuth";
import DeleteModalComponent from '../../components/DeleteModalComponent';
import RecruitmentDataTableUtils from '../../utilities/RecruitmentDataTableUtils';
import { RecruitmentService } from '../../service/RecruitmentService';
import { ProgressSpinner } from 'primereact/progressspinner';
import ErrorMessageDisplayComponent from '../../components/ErrorMessageDisplayComponent';
import ValidationErrorModalComponent from '../../components/ValidationErrorModalComponent';
import useNavProps from '../../hooks/useNavProps';
import RecruitmentPlanToolbar from './RecruitmentPlanToolbar';
import RecruitmentPlanJobEdit from './RecruitmentPlanJobEdit';
import RecruitmentPlanJobSecretEdit from './RecruitmentPlanJobSecretEdit';
import RecruitmentUtils from '../../utilities/RecruitmentUtils';

const RecruitmentPlanJobList = (props) => {

    const [recruitmentPlanJobs, setRecruitmentPlanJobs] = useState([]);
    const [selectedJob, setSelectedJob] = useState(null);
    const [recruitmentPlan, setRecruitmentPlan] = useState(null);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [firstLoading, setFirstLoading] = useState(true);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('generic.error.get.data');
    const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);

    const toast = useRef(null);
    const dt = useRef(null);
    const { auth } = useAuth();

    const { setNavPropsValue } = useNavProps();

    const recruitmentService = useMemo(() => new RecruitmentService(), []);

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: DataTableUtils.defalRowsPerPage(),
        page: 1,
        sortField: 'id',
        sortOrder: 1,
        filters: {
            departmentType: { value: null, matchMode: FilterMatchMode.CONTAINS },
            departmentName: { value: null, matchMode: FilterMatchMode.CONTAINS },
            position: { value: null, matchMode: FilterMatchMode.CONTAINS },
            positionClass: { value: null, matchMode: FilterMatchMode.CONTAINS },
            positionLevel: { value: null, matchMode: FilterMatchMode.CONTAINS },
            jobID: { value: null, matchMode: FilterMatchMode.CONTAINS },
            vacancyForecasted: { value: null, matchMode: FilterMatchMode.EQUALS },
            types: { value: null, matchMode: FilterMatchMode.IN }
        }
    });
    // const [lazyParamsUpdatedFromSession, setLazyParamsUpdatedFromSession] = useState(false);
    const propToColumnMap = {
        departmentType: 'department_type',
        departmentName: 'department_name',
        position: 'position',
        positionClass: 'class',
        positionLevel: 'level',
        jobID: 'job_id',
        vacancyForecasted: 'vacancy_forecasted',
        types: 'type',
        lastModifiedOn: 'last_updated_on'
    };
    const [totalRecords, setTotalRecords] = useState(0);

    const hasPermission = (permissionName) => {
        if (auth && auth.user && auth.user.permissions) {
            return auth.user.permissions.indexOf(permissionName) > -1;
        } else {
            return false;
        }
    };

    useEffect(() => {
        setFirstLoading(true);
        // console.log("type: ", props?.type);

        const getRecruitmentPlan = async () => {
            await recruitmentService.getRecruitmentPlan(props?.recuitmentPlanId)
                .then(async (_data) => {
                    setRecruitmentPlan(_data);

                    setNavPropsValue('recruitment-plan-'+props?.type, _data.id, _data.institution.name)
                    getRecruitmentPlanJobs()

                    setIsLoading(false);
                })
                .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
        };

        getRecruitmentPlan()

        setFirstLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props, recruitmentService]);

    useEffect(() => {
        getRecruitmentPlanJobs()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lazyParams]);

    const setCriteria = () => {
        // GeneralUtils.processSessionFilterValues('RecruitmenPlanJobList', lazyParams, lazyParamsUpdatedFromSession, setLazyParamsUpdatedFromSession);

        let criteria = {};
        criteria.sortOrder = lazyParams.sortOrder < 0 ? 'DESC' : 'ASC';
        criteria.sortField = propToColumnMap[lazyParams.sortField] || 'id';
        criteria.recruitmentPlanId = props?.recuitmentPlanId
        criteria.types = [props?.type];
        criteria.departmentType = lazyParams.filters.departmentType.value;
        criteria.departmentName = lazyParams.filters.departmentName.value;
        criteria.position = lazyParams.filters.position.value;
        criteria.positionClass = lazyParams.filters.positionClass.value;
        criteria.positionLevel = lazyParams.filters.positionLevel.value;
        criteria.jobID = lazyParams.filters.jobID.value;
        criteria.vacancyForecasted = lazyParams.filters.vacancyForecasted.value;

        return criteria;
    };

    const getRecruitmentPlanJobs = async () => {
        setIsLoading(true);

        let criteria = setCriteria();
        criteria.startRow = lazyParams.first;
        criteria.pageSize = lazyParams.rows;

        await recruitmentService.getRecruitmentPlanJobList(criteria)
            .then((_data) => {
                // console.log("job list: ", _data.items)
                setRecruitmentPlanJobs(_data.items);
                setTotalRecords(_data.totalCount);
                setIsLoading(false);
            })
            .catch((error) => {
                setError(true);
                setErrorMessage(error);
            });
    };

    const afterSave = async (savedData, isNew) => {
        toast?.current?.show({ severity: 'success', summary: translatedMessage('generic.save.success'), contentClassName: "pcn-toast-success" });        
        getRecruitmentPlanJobs()
    };

    const showNewRecruitmentPlan = async () => {
        setSelectedJob(null)
        setEditDialogVisible(true);
    };

    const showEditRecruitmentPlan = async (_job) => {
        setSelectedJob(_job)
        setEditDialogVisible(true);
    };

    const showDeleteRecruitmentPlanDialog = (_job) => {
        setSelectedJob(_job);
        setDeleteDialogVisible(true);
    };

    const hideDeleteRecruitmentPlanDialog = () => {
        setDeleteDialogVisible(false);
    };

    const deleteRecruitmentPlan = () => {
        recruitmentService.deleteRecruitmentPlanJob(selectedJob.id)
            .then(
                () => {
                    getRecruitmentPlanJobs()
                    toast?.current?.show({ severity: 'success', summary: translatedMessage('generic.delete.success') });
                },
                (error) => {
                    console.log(error)
                    error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 7000 });
                }
            ).finally(() => {
                setDeleteDialogVisible(false);
                setRecruitmentPlan(null);
            });
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <div className="my-2">
                    {auth?.user?.institutionResponsible &&
                        <Button label={translatedMessage('job.add')}
                            icon="pi pi-plus"
                            className="p-button-primary mr-2"
                            onClick={showNewRecruitmentPlan}
                            disabled={recruitmentPlan?.status !== 'DRAFT'}
                        />
                    }
                </div>
            </React.Fragment>
        );
    };

    // const handleExport = () => {
    //     let exportData = recruitmentCampaigns.map(item => ({ ...item }));
    //     exportData.map(item => {
    //         let exportItem = item
    //         exportItem.status = translatedMessage("RecruitmentStatus." + item.status)
    //         exportItem.lastUpdatedOn = new Date(Date.parse(item.lastUpdatedOn))
    //         delete exportItem.folderId
    //         delete exportItem.fileUuid
    //         delete exportItem.fileName
    //         delete exportItem.description

    //         return item;
    //     })

    //     return exportData
    // }

    const rightToolbarTemplate = () => {
        return (
            <></>
            // <ExportButtonComponent
            //     getExportData={handleExport}
            //     header={tableHeader}
            //     fileName={translatedMessage("recruitment.campaign")}                    
            // />
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions flex flex-wrap align-items-center justify-content-end">
                {(hasPermission('RECRUITMENT_PLAN_EDIT') || auth?.user?.institutionResponsible) &&
                    <Button icon="pi pi-pencil" className="p-button-rounded p-button-info m-1" onClick={() => showEditRecruitmentPlan(rowData)}
                        tooltip={translatedMessage("generic.edit")} tooltipOptions={{ showOnDisabled: true, position: 'top' }} 
                        disabled={rowData.recruitmentPlan.status !== 'DRAFT'} />
                }
                {auth?.user?.institutionResponsible &&
                    <Button icon="pi pi-trash" className="p-button-rounded p-button-danger m-1" onClick={() => showDeleteRecruitmentPlanDialog(rowData)}
                        tooltip={translatedMessage("generic.delete")} tooltipOptions={{ showOnDisabled: true, position: 'top' }}
                        disabled={rowData.recruitmentPlan.status !== 'DRAFT'} />
                }
            </div>
        );
    };

    const onPage = (event) => {
        let _lazyParams = { ...lazyParams };
        _lazyParams.first = event.first;
        _lazyParams.page = event.page;
        _lazyParams.rows = event.rows;
        setLazyParams(_lazyParams);
    };

    const onSort = (event) => {
        let _lazyParams = { ...lazyParams };
        _lazyParams.sortField = event.sortField;
        _lazyParams.sortOrder = event.sortOrder;
        setLazyParams(_lazyParams);
    };

    const onFilter = (event) => {
        event['first'] = 0;
        setLazyParams(event);
    };


    if (firstLoading) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ProgressSpinner />
            </div>
        );
    } else if (error) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ErrorMessageDisplayComponent message={errorMessage} />
            </div>
        );
    } else {
        return (
            <>
                <Toast ref={toast} />
                <div className="grid h-full">
                    <div className="col-12">
                        <div className="card h-full">
                            <div className='w-full grid pcn-data-view-panel'>
                                <div className='col-12 flex flex-row justify-content-between'>
                                    <h4 className='mb-1 font-weight-normal'>{translatedMessage("recruitment.plan") + " - " + recruitmentPlan?.institution?.name}</h4>
                                    <div className='ml-3'>
                                        <div className={`inline-block status status-recruitmenPlan-${recruitmentPlan?.status.toLowerCase()}`}>{translatedMessage("RecruitmentPlanStatus." + recruitmentPlan?.status)}</div>
                                        <span className='ml-2'>{translatedMessage("recruitment.plan.deadline") + ": " + GeneralUtils.formatDate(recruitmentPlan?.deadline)}</span>
                                    </div>
                                </div>
                                <div className='col-12 pt-0'>

                                    <RecruitmentPlanToolbar recruitmentPlan={recruitmentPlan} code={props?.type} />

                                    <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} className="px-0 pt-0"></Toolbar>

                                    <DataTable
                                        ref={dt}
                                        value={recruitmentPlanJobs}
                                        dataKey="id"
                                        paginator
                                        lazy
                                        totalRecords={totalRecords}
                                        first={lazyParams.first}
                                        rows={lazyParams.rows}
                                        sortField={lazyParams.sortField}
                                        sortOrder={lazyParams.sortOrder}
                                        onPage={onPage}
                                        onSort={onSort}
                                        onFilter={onFilter}
                                        rowsPerPageOptions={DataTableUtils.rowsPerPageOptions()}
                                        className="datatable-responsive pcn-datatable"
                                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                        currentPageReportTemplate={'{first} - {last} ' + translatedMessage('generic.of') + ' {totalRecords}'}
                                        emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                                        responsiveLayout="scroll"
                                        filterDisplay="row"
                                        filters={lazyParams.filters}
                                        loading={isLoading}
                                    >
                                        <Column
                                            field="departmentType"
                                            header={translatedMessage('job.departmentType')}
                                            sortable
                                            headerStyle={{ width: '10%', minWidth: '8rem' }}
                                            filter
                                            showFilterMenu={false}
                                            hidden={RecruitmentUtils.hideField(props?.type)}
                                        />
                                        <Column
                                            field="departmentName"
                                            header={translatedMessage('job.departmentName')}
                                            sortable
                                            headerStyle={{ width: '10%', minWidth: '8rem' }}
                                            filter
                                            showFilterMenu={false}
                                            hidden={RecruitmentUtils.hideField(props?.type)}
                                        />
                                        <Column
                                            field="position"
                                            header={translatedMessage('job.position')}
                                            sortable
                                            headerStyle={{ width: RecruitmentUtils.hideField(props?.type) ? '30%' : '10%', minWidth: '8rem' }}
                                            filter
                                            showFilterMenu={false}
                                        />
                                        <Column
                                            field="positionClass"
                                            header={translatedMessage('job.positionClass')}
                                            sortable
                                            headerStyle={{ width: '10%', minWidth: '8rem' }}
                                            filter
                                            showFilterMenu={false}
                                            hidden={RecruitmentUtils.hideField(props?.type)}
                                        />
                                        <Column
                                            field="positionLevel"
                                            header={translatedMessage('job.positionLevel')}
                                            sortable
                                            headerStyle={{ width: '10%', minWidth: '8rem' }}
                                            filter
                                            showFilterMenu={false}
                                            hidden={RecruitmentUtils.hideField(props?.type)}
                                        />
                                        <Column
                                            field="jobID"
                                            header={translatedMessage('job.jobID')}
                                            sortable
                                            headerStyle={{ width: RecruitmentUtils.hideField(props?.type) ? '20%' :  '10%', minWidth: '8rem' }}
                                            filter
                                            showFilterMenu={false}
                                        />
                                        <Column
                                            field="vacancyForecasted"
                                            header={translatedMessage('job.vacancy')}
                                            sortable
                                            headerStyle={{ width: RecruitmentUtils.hideField(props?.type) ? '20%' : '10%', minWidth: '8rem' }}
                                            body={(e) => RecruitmentDataTableUtils.vacancyTemplate(e)}
                                            filter
                                            filterElement={(e) => DataTableUtils.yesNoFilterTemplate(e)}
                                            showFilterMenu={false}
                                        />

                                        <Column body={actionBodyTemplate}></Column>
                                    </DataTable>
                                </div>

                                {/* Edit dialog */}
                                {editDialogVisible && recruitmentPlan?.institution?.secret &&
                                    <RecruitmentPlanJobSecretEdit
                                        value={selectedJob}
                                        recruitmentPlan={recruitmentPlan}
                                        type={props?.type}
                                        visible={editDialogVisible}
                                        afterSave={afterSave}
                                        visibleSetter={setEditDialogVisible}
                                    />
                                }
                                {editDialogVisible && !recruitmentPlan?.institution?.secret &&
                                    <RecruitmentPlanJobEdit
                                        value={selectedJob}
                                        recruitmentPlan={recruitmentPlan}
                                        type={props?.type}
                                        visible={editDialogVisible}
                                        afterSave={afterSave}
                                        visibleSetter={setEditDialogVisible}
                                    />
                                }

                                {deleteDialogVisible &&
                                    <DeleteModalComponent
                                        visible={deleteDialogVisible}
                                        item={selectedJob?.jobID}
                                        closeDialog={hideDeleteRecruitmentPlanDialog}
                                        deleteRecord={() => deleteRecruitmentPlan()}
                                    />
                                }

                                {showValidationErrorDialog &&
                                    <ValidationErrorModalComponent
                                        visible={showValidationErrorDialog}
                                        errors={validationErrors}
                                        dialogTitle={translatedMessage("recruitment.plan.validation.title")}
                                        closeDialog={() => setShowValidationErrorDialog(false)}
                                    />
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
};

export default RecruitmentPlanJobList;
